// Ambience by area and time (decision 0045: "per-area soundscapes, a bed plus occasional spot sounds, following the
// time of day and cross-fading between areas"; suggestion "zone soundscapes and weather coupling"). The game lists its
// beds (a looping cue, the areas and times it plays in, its level, and whether rain brings it in or takes it out) and
// its spot sounds (a one-shot cue, where and when, how often, how far). Pure logic: the engine crossfades each bed to
// the level `bedTargets` gives and plays the spots the scheduler draws, placed around the listener. Seeded, so a test
// can count them.
import { dbToGain, mulberry32, type Rng } from "./dsp";

/** A bed: a looping cue at a level, in some areas and times (absent: everywhere, always). */
export interface BedDef {
  id: string;
  cue: string;
  areas?: string[];
  times?: string[];
  /** dB at full. */
  gain: number;
  /** "with": the bed rises with the rain (the rain itself, drips); "without": it fades as rain comes (birdsong). */
  rain?: "with" | "without";
}

/** A spot sound: a one-shot placed around the listener, at a mean rate, in some areas and times. */
export interface SpotDef {
  id: string;
  cue: string;
  areas?: string[];
  times?: string[];
  /** Mean events per minute (Poisson). */
  perMinute: number;
  /** Distance range from the listener (m). */
  distance: [number, number];
  /** Heard only while rain is under this (0..1; birds go quiet in rain). */
  maxRain?: number;
}

/** What plays where and when. */
export interface AmbienceTable {
  beds: BedDef[];
  spots: SpotDef[];
  /** Crossfade time constant (s) between levels. */
  fade: number;
}

/** The scene's state the ambience follows. */
export interface AmbienceState {
  area: string | null;
  time: string | null;
  /** 0 dry … 1 full rain. */
  rain: number;
}

const inList = (list: string[] | undefined, v: string | null): boolean => !list || (v !== null && list.includes(v));

/** Each bed's target gain (linear) for a state. */
export function bedTargets(beds: readonly BedDef[], s: AmbienceState): Record<string, number> {
  const out: Record<string, number> = {};
  const rain = Math.min(1, Math.max(0, s.rain));
  for (const b of beds) {
    let g = inList(b.areas, s.area) && inList(b.times, s.time) ? dbToGain(b.gain) : 0;
    if (b.rain === "with") g *= rain;
    if (b.rain === "without") g *= 1 - rain;
    out[b.id] = g;
  }
  return out;
}

/** A spot sound to play: the cue, when, and where around the listener (bearing in radians, distance in m). */
export interface SpotEvent {
  spot: string;
  cue: string;
  at: number;
  bearing: number;
  distance: number;
}

/** Draws spot sounds as Poisson events per spot, from a seed. */
export class SpotScheduler {
  private readonly rng: Rng;
  private readonly next = new Map<string, number>();
  private last = -1;
  /** Events drawn so far, by spot. */
  readonly counts: Record<string, number> = {};
  constructor(
    private readonly spots: readonly SpotDef[],
    seed: number,
  ) {
    this.rng = mulberry32(seed);
  }
  private gap(s: SpotDef): number {
    return (-Math.log(1 - this.rng() * 0.999) * 60) / Math.max(1e-6, s.perMinute);
  }
  /** The spots due in (last update, now] for this state. */
  update(now: number, s: AmbienceState): SpotEvent[] {
    const out: SpotEvent[] = [];
    if (this.last < 0) this.last = now;
    for (const sp of this.spots) {
      let t = this.next.get(sp.id) ?? this.last + this.gap(sp);
      const on = inList(sp.areas, s.area) && inList(sp.times, s.time) && s.rain <= (sp.maxRain ?? 1);
      while (t <= now) {
        if (on) {
          out.push({ spot: sp.id, cue: sp.cue, at: t, bearing: this.rng() * Math.PI * 2, distance: sp.distance[0] + this.rng() * (sp.distance[1] - sp.distance[0]) });
          this.counts[sp.id] = (this.counts[sp.id] ?? 0) + 1;
        }
        t += this.gap(sp);
      }
      this.next.set(sp.id, t);
    }
    this.last = now;
    return out;
  }
}
